import { useState, useEffect, useCallback } from 'react';
import {
  FirebaseNotification,
  markAllAsRead,
  markAsRead,
  getAllNotifications,
  NotificationParams,
} from 'wisestep-api';

import { useNotificationSSEStore } from '../components/NotificationSSE/utils/NotificationSSEStore';

export interface UseNotificationsResult {
  notifications: FirebaseNotification[];
  loading: boolean;
  error: Error | null;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  refresh: (params?: NotificationParams) => Promise<void>;
  unreadCount: number;
  totalElements: number;
  totalPages: number;
  currentPage: number;
}

export const useNotifications = (): UseNotificationsResult => {
  const [notifications, setNotifications] = useState<FirebaseNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const setUnreadCount = useNotificationSSEStore((state) => state.setNotificationSSE);
  const unreadCount = useNotificationSSEStore((state) => state.unReadCount);

  const fetchNotifications = useCallback(
    async (params?: NotificationParams) => {
      try {
        setLoading(true);

        // If isRead is undefined, fetch both read and unread
        if (params?.isRead === undefined) {
          const [readResponse, unreadResponse] = await Promise.all([
            getAllNotifications({ ...params, isRead: true }),
            getAllNotifications({ ...params, isRead: false }),
          ]);

          const allNotifications = [...unreadResponse.data.content, ...readResponse.data.content];

          setNotifications(allNotifications);
          setTotalElements(readResponse.data.totalElements + unreadResponse.data.totalElements);
          setTotalPages(Math.max(readResponse.data.totalPages, unreadResponse.data.totalPages));
          setCurrentPage(params?.page || 0);
          setUnreadCount(unreadResponse.data.totalElements);
        } else {
          // Fetch either read or unread based on params.isRead
          const response = await getAllNotifications(params);
          setNotifications(response.data.content);
          setTotalElements(response.data.totalElements);
          setTotalPages(response.data.totalPages);
          setCurrentPage(response.data.number);
          if (!params.isRead) {
            setUnreadCount(response.data.totalElements);
          }
        }

        setError(null);
      } catch (err) {
        console.error('Failed to fetch notifications:', err);
        setError(err instanceof Error ? err : new Error('Failed to fetch notifications'));
      } finally {
        setLoading(false);
      }
    },
    [setUnreadCount]
  );

  const handleMarkAsRead = useCallback(
    async (id: string) => {
      try {
        await markAsRead(id);
        setNotifications((prev) => {
          const newNotifications = prev.filter((notification) => notification.id !== id);
          setTotalElements((total) => total - 1);
          return newNotifications;
        });
        fetchNotifications(); // Refresh to get updated pagination
      } catch (err) {
        console.error('Failed to mark notification as read:', err);
        setError(err instanceof Error ? err : new Error('Failed to mark notification as read'));
        throw err;
      }
    },
    [fetchNotifications]
  );

  const handleMarkAllAsRead = useCallback(async () => {
    try {
      await markAllAsRead();
      setNotifications([]);
      setTotalElements(0);
      setTotalPages(0);
      setCurrentPage(0);
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to mark all notifications as read:', err);
      setError(err instanceof Error ? err : new Error('Failed to mark all notifications as read'));
      throw err;
    }
  }, [setUnreadCount]);

  useEffect(() => {
    // Initial fetch of unread notifications when component mounts
    fetchNotifications({ isRead: false });
  }, [fetchNotifications]);

  return {
    notifications,
    loading,
    error,
    markAsRead: handleMarkAsRead,
    markAllAsRead: handleMarkAllAsRead,
    refresh: fetchNotifications,
    unreadCount,
    totalElements,
    totalPages,
    currentPage,
  };
};
